import React, { useState, useEffect } from 'react';
import { Snackbar, Box, IconButton, Portal } from '@mui/material';
import BoxButton from '@/components/FileViewer/@components/BoxButton';
import ToolbarTooltip from '@/components/FileViewer/@components/ToolbarTooltip';
import FullscreenIcon from '@/public/icons/files/expand.svg';
import CloseIcon from '@mui/icons-material/Close';

interface ViewModeToggleProps {
  onToggle: () => void;
  isMobile?: boolean;
}

const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  onToggle,
  isMobile = false,
}) => {
  const [showToast, setShowToast] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;

      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange,
      );
    };
  }, []);

  const handleToggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        // 전체화면 진입
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if ((elem as any).webkitRequestFullscreen) {
          await (elem as any).webkitRequestFullscreen();
        } else if ((elem as any).mozRequestFullScreen) {
          await (elem as any).mozRequestFullScreen();
        } else if ((elem as any).msRequestFullscreen) {
          await (elem as any).msRequestFullscreen();
        }
        // 모바일에는 ESC 키가 없으므로 안내 토스트를 띄우지 않는다.
        if (!isMobile) {
          setShowToast(true);
        }
      } else {
        // 전체화면 해제
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
      onToggle();
    } catch (error) {
      console.error('Fullscreen toggle failed:', error);
    }
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  return (
    <>
      <ToolbarTooltip title={isFullscreen ? '확장 해제' : '확장해서 보기'}>
        <BoxButton
          icon={<FullscreenIcon />}
          boxSize={48}
          iconSize={24}
          onClick={handleToggleFullscreen}
          selected={isFullscreen}
        />
      </ToolbarTooltip>

      {/* 전체화면 안내 토스트.
          Snackbar는 Portal을 쓰지 않아 렌더된 자리에 남는데, 부모 FloatingToolbar에
          transform이 걸려 있어 position:fixed 기준이 뷰포트가 아닌 툴바가 된다.
          Portal로 body에 붙여야 top:80px이 뷰포트 기준으로 잡힌다. */}
      <Portal>
        <Snackbar
          open={showToast}
          autoHideDuration={3000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            top: '80px !important',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(61, 67, 73, 0.95)',
              color: '#FFFFFF',
              padding: '12px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
            }}
          >
            ESC 키를 눌러 전체보기를 종료할 수 있습니다.
            <IconButton
              size="small"
              onClick={handleCloseToast}
              sx={{
                color: '#FFFFFF',
                padding: '4px',
                marginLeft: '8px',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Snackbar>
      </Portal>
    </>
  );
};

export default ViewModeToggle;
